import React, { useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const OUTCOMES = ["Completed", "Rebooked", "Ghosted", "Canceled"];

/**
 * The outcomes after which another Appointment can legitimately follow.
 *
 * Both of these describe an Appointment that HAPPENED. Rebooked says it
 * produced a replacement; Completed says it concluded successfully -- and a
 * completed meeting very often still needs the next one, an application, a
 * review, a second consultation. Ghosted and Canceled describe an Appointment
 * that did not happen, so there is nothing to follow on from here.
 *
 * Declared beside OUTCOMES because it partitions exactly that vocabulary: add
 * an outcome above and this is the line that has to decide where it belongs.
 */
const FOLLOW_UP_ELIGIBLE_OUTCOMES = ["Completed", "Rebooked"];
const CUSTOMER_STATUSES = ["Sold", "Scheduled", "Ghosted", "Dead Deal", "No Scheduled CB"];
const PRODUCTS = ["IUL", "Annuity", "Term", "Final Expense", "None", "Other"];

function newBookingOperationId() {
  // Same browser-native convention as the rest of the app; no new dependency.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `bk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export default function AppointmentSummaryDialog({
  open,
  onOpenChange,
  appointment,
  onSubmitted,
  onScheduleAfterSubmit = () => {},
  readOnly = false,
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Stable across retries of this summary submission, so a lost response
  // cannot mint a second follow-up Appointment. Minted outside the submit
  // handler for exactly that reason.
  const [followUpOperationId, setFollowUpOperationId] = useState(() => newBookingOperationId());

  const [outcome, setOutcome] = useState("Completed");
  const [productsPresented, setProductsPresented] = useState([]);
  const [summary, setSummary] = useState("");
  const [customerStatus, setCustomerStatus] = useState("Scheduled");
  const [scheduleAfterSubmit, setScheduleAfterSubmit] = useState(null);

  // The scheduling question belongs to the OUTCOME -- what this Appointment
  // produced -- and never to customerStatus, which describes the CUSTOMER.
  // Gating it on `customerStatus === "Scheduled"` meant an agent recording
  // Rebooked alongside Sold, Dead Deal, Ghosted or No Scheduled CB was never
  // asked whether to book the next Appointment.
  //
  // It is the eligible CLASS rather than Rebooked alone: forcing an agent to
  // misclassify a successful meeting as Rebooked, purely to be offered the
  // scheduling step, would corrupt the outcome record to reach a workflow.
  //
  // One predicate, named once, so the render, the validation and the reset
  // cannot drift apart -- and so moving between two eligible outcomes is a
  // no-op for scheduling intent rather than something each site decides.
  const schedulingQuestionApplies = FOLLOW_UP_ELIGIBLE_OUTCOMES.includes(outcome);

  // What the form still needs. Named once and read by BOTH the Submit button
  // and the guards below, so the control's enabled state and the message the
  // user gets can never describe different requirements.
  //
  // Submit was previously gated only on `saving`: every requirement lived in
  // the handler, so the button invited a click and then refused it. The
  // handler keeps its specific messages -- they say WHICH field is missing,
  // which a disabled button cannot -- but they now read these same values
  // rather than re-deriving the conditions.
  //
  // `outcome` and `customerStatus` are seeded non-empty and the pills only
  // ever set a listed value, so neither can actually be blank today. The
  // outcome term is kept because the contract names it and a future empty
  // initial state must not silently pass.
  const hasOutcome = Boolean(outcome);
  const hasProducts = productsPresented.length > 0;
  const hasSummary = Boolean(summary.trim());
  // The scheduling answer is required only while the question is on screen --
  // the same predicate that renders it, so an invisible question can never
  // block Submit.
  const hasSchedulingAnswer =
    !schedulingQuestionApplies || Boolean(scheduleAfterSubmit);

  const canSubmit = hasOutcome && hasProducts && hasSummary && hasSchedulingAnswer;

  const optionButtonClass = (selected) =>
    [
      "h-8 rounded-full px-3 text-xs font-semibold transition",
      selected
        ? "bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
        : "bg-white/15 text-slate-100 hover:bg-white/20",
    ].join(" ");


  const title = useMemo(() => {
    const name = appointment?.lead_snapshot?.full_name;
    return name ? `Appointment Summary — ${name}` : "Appointment Summary";
  }, [appointment]);

  const isReadOnly = readOnly;

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setOutcome("Completed");
      setProductsPresented([]);
      setSummary("");
      setCustomerStatus("Scheduled");
      setScheduleAfterSubmit(null);
      return;
    }

    // if there is existing payload, prefill
    const payload = appointment?.summary_payload;
    if (payload) {
      setOutcome(payload.appointment_outcome || "Completed");
      setProductsPresented(payload.products_presented || []);
      setSummary(payload.summary || "");
      setCustomerStatus(payload.customer_status || "Scheduled");
      if (payload.follow_up_scheduled === true) {
        setScheduleAfterSubmit("yes");
      } else if (payload.follow_up_scheduled === false) {
        setScheduleAfterSubmit("no");
      } else {
        setScheduleAfterSubmit(null);
      }
    }
  }, [open, appointment]);

  // Drop a stale Yes/No when the question stops applying. Without this, an
  // agent who picked Yes and then changed the outcome away from Rebooked would
  // still submit follow_up_scheduled=true and be sent into scheduling for an
  // Appointment that no longer claims to need one.
  //
  // Deliberately keyed on the outcome alone: changing customer_status while
  // the outcome is still Rebooked must NOT discard the answer, which is what
  // the previous customerStatus-keyed version did.
  useEffect(() => {
    if (!schedulingQuestionApplies) {
      setScheduleAfterSubmit(null);
    }
  }, [schedulingQuestionApplies]);

  const toggleProduct = (p) => {
    if (isReadOnly) return;
    setProductsPresented((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const submit = async () => {
    if (isReadOnly) {
      onOpenChange(false);
      return;
    }
    if (!hasSummary) {
      toast({ title: "Summary is required", variant: "destructive" });
      return;
    }
    if (!customerStatus) {
      toast({ title: "Customer Status is required", variant: "destructive" });
      return;
    }
    if (!hasProducts) {
      toast({ title: "Products Presented is required", variant: "destructive" });
      return;
    }
    if (!hasSchedulingAnswer) {
      toast({ title: "Please choose Yes or No", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const res = await api.post(`/appointments/${appointment.id}/summary`, {
        appointment_outcome: outcome,
        products_presented: productsPresented,
        summary: summary.trim(),
        customer_status: customerStatus,
        follow_up_scheduled: scheduleAfterSubmit === "yes",
        follow_up_at: undefined,
        follow_up_booking_operation_id: followUpOperationId,
      });
      setFollowUpOperationId(newBookingOperationId());

      toast({ title: "Summary submitted" });
      onOpenChange(false);
      onSubmitted?.();
      if (scheduleAfterSubmit === "yes") {
        // Hand back the follow-up the server actually created, if any. The id
        // is already in the response; it is never rediscovered client-side.
        // When none was created (this dialog does not collect a follow-up
        // time), the caller opens follow-up CREATION -- never a reschedule of
        // the appointment just completed.
        onScheduleAfterSubmit?.(
          res?.data?.summary_payload?.follow_up_appointment_id || null
        );
      }
    } catch (e) {
      toast({
        title: "Could not submit summary",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl" data-testid="appointment-summary-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-200/80">Appointment Outcome <span className="text-xs text-slate-200/60">Required</span></Label>
            <div className="flex flex-wrap gap-2">
              {OUTCOMES.map((v) => (
                <Button
                  key={v}
                  type="button"
                  variant="secondary"
                  className={optionButtonClass(outcome === v)}
                  onClick={() => setOutcome(v)}
                  disabled={isReadOnly}
                  data-testid={`appointment-summary-outcome-${v.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {v}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Products Presented <span className="text-xs text-slate-200/60">Required</span></Label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {PRODUCTS.map((p) => (
                <label key={p} className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 ring-1 ring-white/10" data-testid={`appointment-summary-product-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Checkbox
                    checked={productsPresented.includes(p)}
                    onCheckedChange={() => toggleProduct(p)}
                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                    disabled={isReadOnly}
                    data-testid={`appointment-summary-product-checkbox-${p.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                  <span className="text-sm text-slate-100" data-testid={`appointment-summary-product-label-${p.toLowerCase().replace(/\s+/g, "-")}`}>{p}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Summary of Appointment <span className="text-xs text-slate-200/60">Required</span></Label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="min-h-[120px] bg-white/5 text-slate-100 ring-1 ring-white/10"
              placeholder="Enter summary..."
              readOnly={isReadOnly}
              data-testid="appointment-summary-textarea"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Appointment Status <span className="text-xs text-slate-200/60">Required</span></Label>
            <div className="flex flex-wrap gap-2">
              {CUSTOMER_STATUSES.map((v) => (
                <Button
                  key={v}
                  type="button"
                  variant="secondary"
                  className={optionButtonClass(customerStatus === v)}
                  onClick={() => setCustomerStatus(v)}
                  disabled={isReadOnly}
                  data-testid={`appointment-summary-customer-status-${v.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {v}
                </Button>
              ))}
            </div>
          </div>

          {schedulingQuestionApplies && (
            <div className="space-y-3 rounded-2xl bg-white/5 px-4 py-3 ring-1 ring-white/10" data-testid="appointment-summary-follow-up-question">
              <div className="text-sm font-medium text-slate-100">Schedule appointment after submitting Appointment Summary?</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className={optionButtonClass(scheduleAfterSubmit === "yes")}
                  onClick={() => setScheduleAfterSubmit("yes")}
                  disabled={isReadOnly}
                  data-testid="appointment-summary-follow-up-yes"
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className={optionButtonClass(scheduleAfterSubmit === "no")}
                  onClick={() => setScheduleAfterSubmit("no")}
                  disabled={isReadOnly}
                  data-testid="appointment-summary-follow-up-no"
                >
                  No
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="appointment-summary-cancel"
          >
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {isReadOnly ? null : (
            <Button onClick={submit} disabled={saving || !canSubmit} data-testid="appointment-summary-submit">
              {saving ? "Submitting…" : "Submit"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
